import React, { useEffect, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel,
    MenuItem, Select, TextField, Typography,
} from '@mui/material';
import Axios from 'axios';
import { useSnackbar } from 'notistack';
import { getAuthHeader } from '../../utils/authToken';
import { firstInvalidEmail, PARENT_EMAIL_LABELS } from '../../utils/emailShape';
import { countryPhoneCodes } from '../weekly/studentButtonMenu/countryPhoneCode';
import { formatPhone } from '../../voice/callFormat';
import { missedCallsApi, errorMessage } from './missedCallsApi';
import { voiceApi } from '../../voice/voiceApi';
import { uploadedDay, splitE164, US_PHONE_CODE } from './missedCallFormat';

const API = process.env.REACT_APP_URL;
const auth = () => ({ headers: { Authorization: getAuthHeader() } });
const DEFAULT_EMAIL_CODE = 'MK1';

/**
 * A new (potential) student from a missed call, with the number already on the card — the same six fields and
 * the same rules as the Topbar's Quick Add (`POST /students/quick-add`), so there is one way to create one.
 * On success the row's Who is set to the new student, which is the whole point of doing it from here.
 */
const NewStudentDialog = ({ row, open, onClose, onSaved }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [emailCode, setEmailCode] = useState(DEFAULT_EMAIL_CODE);
    const [consultationNumber, setConsultationNumber] = useState('');
    const [country, setCountry] = useState(US_PHONE_CODE);
    const [phone, setPhone] = useState('');
    const [parentEmail, setParentEmail] = useState('');
    const [gender, setGender] = useState('');
    const [error, setError] = useState(null);
    const [takenWarning, setTakenWarning] = useState(null);   // the number is in use: press again to go ahead
    const [busy, setBusy] = useState(false);
    // { uuid, number } once Quick Add has succeeded: the student exists, so another press only retries what follows it
    const [created, setCreated] = useState(null);

    useEffect(() => {
        if (!open || !row) return;
        const { country: c, phone: p } = splitE164(row.phone_number);
        setEmailCode(DEFAULT_EMAIL_CODE);
        setCountry(c);
        setPhone(p);
        setParentEmail('');
        setGender('');
        setError(null);
        setTakenWarning(null);
        setCreated(null);
        setConsultationNumber('');
        // Suggest the next number, the way the office numbers a new family.
        Axios.get(`${API}/api/v1/students/consultation-number/last-number`, auth())
            .then((res) => { const n = Number(res.data); if (Number.isFinite(n)) setConsultationNumber(String(n + 1)); })
            .catch(() => {});
    }, [open, row]);

    if (!row) return null;

    /** The family rule: a known parent email may only be reused with that family's own consultation number. */
    const familyConflict = async () => {
        const email = parentEmail.trim();
        if (!email) return null;
        try {
            const { data } = await Axios.get(`${API}/api/v1/students/parent-email/check`, { params: { email }, ...auth() });
            if (!data.exists) return null;
            const numbers = [...new Set(data.consultation_numbers || [])];
            if (consultationNumber && numbers.some((n) => Number(n) === Number(consultationNumber))) return null;
            return numbers.length
                ? `이 학부모 이메일은 이미 상담번호 ${numbers.join(', ')}로 있습니다. 새 번호 대신 그 번호를 쓰세요.`
                : '이 학부모 이메일은 이미 있습니다 (상담번호 없음).';
        } catch {
            return null; // the server enforces the same rule on submit
        }
    };

    /** Quick Add → { uuid, number }, or null when it stopped to ask first (a family conflict, a number already in use). */
    const createStudent = async () => {
        const number = consultationNumber.trim();
        const conflict = await familyConflict();
        if (conflict) { setError(conflict); return null; }

        if (number && !takenWarning) {
            const { data } = await Axios.get(`${API}/api/v1/students/consultation-number/check/${number}`, auth());
            if (data.exists) {
                setTakenWarning(`상담번호 ${number}은(는) 이미 있습니다. 그래도 만들려면 다시 누르세요.`);
                return null;
            }
        }

        const { data } = await Axios.post(`${API}/api/v1/students/quick-add`, {
            email_code: emailCode.trim() || null,
            consultation_number: number || null,
            parent_phone: phone.trim(),
            parent_email: parentEmail.trim(),
            parent_gender: gender,
            parent_country_phone_code: country ? country.code : '+1',
        }, auth());
        return { uuid: data.student_uuid, number: number || null };
    };

    const submit = async () => {
        setError(null);
        if (!created) {
            const number = consultationNumber.trim();
            if (number && !/^\d+$/.test(number)) return setError('상담번호는 숫자만 입력하세요.');
            if (phone.trim() && !country) return setError('국가 번호를 고르세요.');
            const emailError = firstInvalidEmail({ parent_email: parentEmail.trim() }, PARENT_EMAIL_LABELS);
            if (emailError) return setError(emailError);
        }

        setBusy(true);
        try {
            let student = created;
            if (!student) {
                try {
                    student = await createStudent();
                } catch (err) {
                    setError(errorMessage(err));
                    return;
                }
                if (!student) return;
                setCreated(student);
            }

            try {
                // new_student: 기록 reads "새 학생 만들어 Who 지정" rather than a plain pick.
                const out = await missedCallsApi.setWho(row.id, { student_uuid: student.uuid, new_student: true });
                onSaved(out.row);
            } catch (err) {
                // The student is there either way; saying only "error" invites a second press and a second student.
                const made = student.number ? `학생은 만들었습니다 (상담번호 ${student.number})` : '학생은 만들었습니다';
                setError(`${made}. 이 행의 Who로 지정하지 못했습니다: ${errorMessage(err)} — 다시 누르면 지정만 다시 합니다.`);
                return;
            }

            // Attach them to the call as well, so the AI summary becomes a memo on their card
            // (`createCallMemo` refuses a call with no student, and the call was placed before this student
            // existed). That memo is what the 학생카드반영 badge hangs the 안내 메일 task on. The row refreshes
            // itself when the memo lands — the model hooks watch call_logs.memo_id.
            const callId = row.connected_call?.id;
            if (callId) {
                try {
                    await voiceApi.updateCall(callId, { student_uuid: student.uuid });
                } catch (err) {
                    // Attaching needs the voice permission, and a non-manager may only touch calls they placed.
                    enqueueSnackbar(`학생을 만들고 Who를 지정했지만 통화에는 연결하지 못했습니다: ${errorMessage(err)}`, { variant: 'warning' });
                    onClose();
                    return;
                }
            }
            enqueueSnackbar(
                callId ? '학생을 만들고 통화에 연결했습니다' : '학생을 만들고 이 번호를 지정했습니다',
                { variant: 'success' },
            );
            onClose();
        } finally {
            setBusy(false);
        }
    };

    // Once the student exists its fields no longer mean anything: only the Who is left to retry.
    const locked = busy || Boolean(created);
    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" disableEnforceFocus>
            <DialogTitle sx={{ pb: 0.5 }}>
                새 학생 만들기
                <Typography variant="body2" color="text.secondary">
                    {uploadedDay(row)} #{row.daily_seq} · {formatPhone(row.phone_number) || row.phone_number} → 학부모 번호로 저장됩니다
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField label="Email Code" value={emailCode} onChange={(e) => setEmailCode(e.target.value)} size="small" disabled={locked} />
                    <TextField
                        label="Consultation Number"
                        value={consultationNumber}
                        onChange={(e) => { setConsultationNumber(e.target.value); setTakenWarning(null); }}
                        size="small"
                        inputProps={{ inputMode: 'numeric' }}
                        disabled={locked}
                    />
                    <Autocomplete
                        options={countryPhoneCodes}
                        value={country}
                        onChange={(e, v) => setCountry(v)}
                        getOptionLabel={(o) => `${o.code} ${o.name}`}
                        isOptionEqualToValue={(o, v) => o.code === v.code && o.name === v.name}
                        renderInput={(params) => <TextField {...params} label="Country Phone Code" size="small" />}
                        disabled={locked}
                    />
                    <TextField label="Parent's Phone" value={phone} onChange={(e) => setPhone(e.target.value)} size="small" inputProps={{ inputMode: 'tel' }} disabled={locked} />
                    <TextField label="Parent's Email" value={parentEmail} onChange={(e) => { setParentEmail(e.target.value); setError(null); }} size="small" disabled={locked} />
                    <FormControl size="small" disabled={locked}>
                        <InputLabel id="missed-call-parent-gender">Parent's Gender</InputLabel>
                        <Select labelId="missed-call-parent-gender" label="Parent's Gender" value={gender} onChange={(e) => setGender(e.target.value)}>
                            <MenuItem value=""><em>None</em></MenuItem>
                            <MenuItem value="Male">Male</MenuItem>
                            <MenuItem value="Female">Female</MenuItem>
                        </Select>
                    </FormControl>
                    {takenWarning && <Alert severity="warning">{takenWarning}</Alert>}
                    {error && <Alert severity={created ? 'warning' : 'error'}>{error}</Alert>}
                    <Typography variant="caption" color="text.secondary">
                        Topbar의 Quick Add와 같은 방식으로 잠재 학생(potential student)이 만들어집니다.
                        {row.connected_call?.id
                            ? ' 이 행의 통화에도 연결되어, AI 요약이 학생 카드의 메모로 들어갑니다. 신규문의(안내 메일) 태스크는 그 메모의 학생카드반영 배지에서 추가하세요.'
                            : ' 아직 통화가 없어 학생 카드에 메모는 생기지 않습니다.'}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>취소</Button>
                <Button variant="contained" onClick={submit} disabled={busy}>
                    {busy ? (created ? '지정 중…' : '만드는 중…') : created ? 'Who 다시 지정' : takenWarning ? '그래도 만들기' : '만들고 지정'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewStudentDialog;
